import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BellIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/store/languageStore';
import PersonalTrackerAlarmModal, { type TrackerAlarm } from './PersonalTrackerAlarmModal';

const FEATURE = 'DEQUE_PERSONAL_TRACKERS';
const POLL_MS = 12_000;

export default function PersonalTrackerAlarmBell({ iconColor }: { iconColor: string }) {
  const { t } = useTranslation();
  const { hasFeatureAccess } = usePermissions();
  const enabled = hasFeatureAccess(FEATURE);
  const [alarms, setAlarms] = useState<TrackerAlarm[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await api.get<{ success: boolean; data: TrackerAlarm[] }>(
        '/api/deque/personal-trackers/alarms',
      );
      setAlarms(response.data.data ?? []);
    } catch {
      setAlarms([]);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, load]);

  const ack = async (id: string) => {
    setAcking(true);
    try {
      await api.post(`/api/deque/personal-trackers/alarms/${id}/ack`);
      const next = alarms.filter((alarm) => alarm.id !== id);
      setAlarms(next);
      setSelectedId(next[0]?.id ?? null);
      toast.success(t('personalTrackers.alarmAcked'));
      if (next.length === 0) setOpen(false);
    } catch {
      toast.error(t('personalTrackers.saveError'));
    } finally {
      setAcking(false);
    }
  };

  const blinking = enabled && alarms.length > 0;

  return (
    <>
      <button
        type="button"
        className="relative h-8 w-8 cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
        aria-label={blinking ? t('personalTrackers.alarmBell') : 'Notifications'}
        onClick={() => {
          if (!enabled) return;
          setSelectedId(alarms[0]?.id ?? null);
          setOpen(true);
          void load();
        }}
      >
        <span
          className={blinking ? 'personal-tracker-alarm-blink inline-flex' : 'inline-flex'}
          style={blinking ? undefined : { color: iconColor }}
        >
          <BellIcon className="w-5 h-5" />
        </span>
        {blinking && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-red-600 px-1 text-[10px] leading-4 text-white">
            {alarms.length}
          </span>
        )}
      </button>
      {open && enabled && (
        <PersonalTrackerAlarmModal
          alarms={alarms}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onAck={(id) => void ack(id)}
          acking={acking}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
